import { useCallback } from 'react';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import { useCreateListMutation } from '@/features/customListSlice';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { LISTS_BASE_PATH } from '@/app/constants';
import ListDetailsForm, { type NewListForm } from './ListDetailsForm';
import { useRunListAction } from './customListSelectorUtils';

interface CreateProjectModalProps {
  opened: boolean;
  onClose: () => void;
  /** Analytics event to track when the project is saved */
  analyticsEvent?: string;
  /** Additional analytics params */
  analyticsParams?: Record<string, string | number>;
  /** Custom title for the modal */
  title?: string;
  /** Custom submit button text */
  submitButtonText?: string;
  /** Step information for multi-step flows */
  stepInfo?: {
    onStep: number;
    ofStep: number;
    action?: string;
  };
}

export function CreateProjectModal({
  opened,
  onClose,
  analyticsEvent = GoogleEvents.projects.creation.newProjectSaved,
  analyticsParams = {},
  title,
  submitButtonText,
  stepInfo,
}: CreateProjectModalProps) {
  const navigateWithSearch = useNavigateWithSearch();
  const intl = useClientTerminology();
  const [createList, { isLoading }] = useCreateListMutation();
  const runListAction = useRunListAction(onClose, {
    successTitle: intl.formatMessage({ id: 'action.success' }),
    successMessage: intl.formatMessage({ id: 'customList.createListSuccess' }),
  });

  const handleSubmit = useCallback(
    async (values: NewListForm) => {
      TagManager.dataLayer({
        dataLayer: {
          event: analyticsEvent,
          params: {
            projectName: values.name,
            projectDescription: values.description,
            ...analyticsParams,
          },
        },
      });

      const result = await runListAction(createList(values).unwrap);

      if (result) {
        navigateWithSearch(`${LISTS_BASE_PATH}/${result.summary.id}`);
      }
    },
    [
      analyticsEvent,
      analyticsParams,
      createList,
      runListAction,
      navigateWithSearch,
    ]
  );

  return (
    <ListDetailsForm
      opened={opened}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSaving={isLoading}
      title={
        title ||
        (stepInfo
          ? intl.formatMessage(
              { id: 'customList.steps' },
              {
                onStep: stepInfo.onStep,
                ofStep: stepInfo.ofStep,
                action:
                  stepInfo.action ||
                  intl.formatMessage({
                    id: 'customList.createList',
                  }),
              }
            )
          : intl.formatMessage({
              id: 'customList.createList',
            }))
      }
      submitButtonText={
        submitButtonText ||
        intl.formatMessage({
          id: 'customList.createList',
        })
      }
    />
  );
}
